import { createPromiseClient, createCallbackClient } from '@bufbuild/connect';
import { createConnectTransport } from '@bufbuild/connect-web';
import { proto3, Struct, Message, protoInt64, MethodKind } from '@bufbuild/protobuf';
import { ProviderStatus, OpenFeatureEventEmitter, FlagNotFoundError, TypeMismatchError, StandardResolutionReasons, OpenFeature, ProviderEvents } from '@openfeature/web-sdk';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

//*
/**
 * Request body for bulk flag evaluation, used by the ResolveAll rpc.
 *
 * @generated from message schema.v1.ResolveAllRequest
 */
class ResolveAllRequest extends Message {
    constructor(data) {
        super();
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new ResolveAllRequest().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new ResolveAllRequest().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new ResolveAllRequest().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(ResolveAllRequest, a, b);
    }
}
ResolveAllRequest.runtime = proto3;
ResolveAllRequest.typeName = "schema.v1.ResolveAllRequest";
ResolveAllRequest.fields = proto3.util.newFieldList(() => [
    { no: 1, name: "context", kind: "message", T: Struct },
]);
/**
 * Response body for bulk flag evaluation, used by the ResolveAll rpc.
 *
 * @generated from message schema.v1.ResolveAllResponse
 */
class ResolveAllResponse extends Message {
    constructor(data) {
        super();
        /**
         * Object structure describing the evaluated flags for the provided context.
         *
         * @generated from field: map<string, schema.v1.AnyFlag> flags = 1;
         */
        this.flags = {};
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new ResolveAllResponse().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new ResolveAllResponse().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new ResolveAllResponse().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(ResolveAllResponse, a, b);
    }
}
ResolveAllResponse.runtime = proto3;
ResolveAllResponse.typeName = "schema.v1.ResolveAllResponse";
ResolveAllResponse.fields = proto3.util.newFieldList(() => [
    { no: 1, name: "flags", kind: "map", K: 9 /* ScalarType.STRING */, V: { kind: "message", T: AnyFlag } },
]);
/**
 * A variant type flag response.
 *
 * @generated from message schema.v1.AnyFlag
 */
class AnyFlag extends Message {
    constructor(data) {
        super();
        /**
         * The reason for the given return value, see https://openfeature.dev/docs/specification/types#resolution-details
         *
         * @generated from field: string reason = 1;
         */
        this.reason = "";
        /**
         * The variant name of the returned flag value.
         *
         * @generated from field: string variant = 2;
         */
        this.variant = "";
        /**
         * The response value of the boolean flag evaluation, will be unset in the case of error.
         *
         * @generated from oneof schema.v1.AnyFlag.value
         */
        this.value = { case: undefined };
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new AnyFlag().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new AnyFlag().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new AnyFlag().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(AnyFlag, a, b);
    }
}
AnyFlag.runtime = proto3;
AnyFlag.typeName = "schema.v1.AnyFlag";
AnyFlag.fields = proto3.util.newFieldList(() => [
    { no: 1, name: "reason", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 2, name: "variant", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 3, name: "bool_value", kind: "scalar", T: 8 /* ScalarType.BOOL */, oneof: "value" },
    { no: 4, name: "string_value", kind: "scalar", T: 9 /* ScalarType.STRING */, oneof: "value" },
    { no: 5, name: "double_value", kind: "scalar", T: 1 /* ScalarType.DOUBLE */, oneof: "value" },
    { no: 6, name: "object_value", kind: "message", T: Struct, oneof: "value" },
]);
/**
 * Request body for boolean flag evaluation, used by the ResolveBoolean rpc.
 *
 * @generated from message schema.v1.ResolveBooleanRequest
 */
class ResolveBooleanRequest extends Message {
    constructor(data) {
        super();
        /**
         * Flag key of the requested flag.
         *
         * @generated from field: string flag_key = 1;
         */
        this.flagKey = "";
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new ResolveBooleanRequest().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new ResolveBooleanRequest().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new ResolveBooleanRequest().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(ResolveBooleanRequest, a, b);
    }
}
ResolveBooleanRequest.runtime = proto3;
ResolveBooleanRequest.typeName = "schema.v1.ResolveBooleanRequest";
ResolveBooleanRequest.fields = proto3.util.newFieldList(() => [
    { no: 1, name: "flag_key", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 2, name: "context", kind: "message", T: Struct },
]);
/**
 * Response body for boolean flag evaluation. used by the ResolveBoolean rpc.
 *
 * @generated from message schema.v1.ResolveBooleanResponse
 */
class ResolveBooleanResponse extends Message {
    constructor(data) {
        super();
        /**
         * The response value of the boolean flag evaluation, will be unset in the case of error.
         *
         * @generated from field: bool value = 1;
         */
        this.value = false;
        /**
         * The reason for the given return value, see https://openfeature.dev/docs/specification/types#resolution-details
         *
         * @generated from field: string reason = 2;
         */
        this.reason = "";
        /**
         * The variant name of the returned flag value.
         *
         * @generated from field: string variant = 3;
         */
        this.variant = "";
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new ResolveBooleanResponse().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new ResolveBooleanResponse().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new ResolveBooleanResponse().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(ResolveBooleanResponse, a, b);
    }
}
ResolveBooleanResponse.runtime = proto3;
ResolveBooleanResponse.typeName = "schema.v1.ResolveBooleanResponse";
ResolveBooleanResponse.fields = proto3.util.newFieldList(() => [
    { no: 1, name: "value", kind: "scalar", T: 8 /* ScalarType.BOOL */ },
    { no: 2, name: "reason", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 3, name: "variant", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 4, name: "metadata", kind: "message", T: Struct },
]);
/**
 * Request body for string flag evaluation, used by the ResolveString rpc.
 *
 * @generated from message schema.v1.ResolveStringRequest
 */
class ResolveStringRequest extends Message {
    constructor(data) {
        super();
        /**
         * Flag key of the requested flag.
         *
         * @generated from field: string flag_key = 1;
         */
        this.flagKey = "";
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new ResolveStringRequest().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new ResolveStringRequest().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new ResolveStringRequest().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(ResolveStringRequest, a, b);
    }
}
ResolveStringRequest.runtime = proto3;
ResolveStringRequest.typeName = "schema.v1.ResolveStringRequest";
ResolveStringRequest.fields = proto3.util.newFieldList(() => [
    { no: 1, name: "flag_key", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 2, name: "context", kind: "message", T: Struct },
]);
/**
 * Response body for string flag evaluation. used by the ResolveString rpc.
 *
 * @generated from message schema.v1.ResolveStringResponse
 */
class ResolveStringResponse extends Message {
    constructor(data) {
        super();
        /**
         * The response value of the string flag evaluation, will be unset in the case of error.
         *
         * @generated from field: string value = 1;
         */
        this.value = "";
        /**
         * The reason for the given return value, see https://openfeature.dev/docs/specification/types#resolution-details
         *
         * @generated from field: string reason = 2;
         */
        this.reason = "";
        /**
         * The variant name of the returned flag value.
         *
         * @generated from field: string variant = 3;
         */
        this.variant = "";
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new ResolveStringResponse().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new ResolveStringResponse().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new ResolveStringResponse().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(ResolveStringResponse, a, b);
    }
}
ResolveStringResponse.runtime = proto3;
ResolveStringResponse.typeName = "schema.v1.ResolveStringResponse";
ResolveStringResponse.fields = proto3.util.newFieldList(() => [
    { no: 1, name: "value", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 2, name: "reason", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 3, name: "variant", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 4, name: "metadata", kind: "message", T: Struct },
]);
/**
 * Request body for float flag evaluation, used by the ResolveFloat rpc.
 *
 * @generated from message schema.v1.ResolveFloatRequest
 */
class ResolveFloatRequest extends Message {
    constructor(data) {
        super();
        /**
         * Flag key of the requested flag.
         *
         * @generated from field: string flag_key = 1;
         */
        this.flagKey = "";
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new ResolveFloatRequest().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new ResolveFloatRequest().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new ResolveFloatRequest().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(ResolveFloatRequest, a, b);
    }
}
ResolveFloatRequest.runtime = proto3;
ResolveFloatRequest.typeName = "schema.v1.ResolveFloatRequest";
ResolveFloatRequest.fields = proto3.util.newFieldList(() => [
    { no: 1, name: "flag_key", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 2, name: "context", kind: "message", T: Struct },
]);
/**
 * Response body for float flag evaluation. used by the ResolveFloat rpc.
 *
 * @generated from message schema.v1.ResolveFloatResponse
 */
class ResolveFloatResponse extends Message {
    constructor(data) {
        super();
        /**
         * The response value of the float flag evaluation, will be empty in the case of error.
         *
         * @generated from field: double value = 1;
         */
        this.value = 0;
        /**
         * The reason for the given return value, see https://openfeature.dev/docs/specification/types#resolution-details
         *
         * @generated from field: string reason = 2;
         */
        this.reason = "";
        /**
         * The variant name of the returned flag value.
         *
         * @generated from field: string variant = 3;
         */
        this.variant = "";
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new ResolveFloatResponse().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new ResolveFloatResponse().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new ResolveFloatResponse().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(ResolveFloatResponse, a, b);
    }
}
ResolveFloatResponse.runtime = proto3;
ResolveFloatResponse.typeName = "schema.v1.ResolveFloatResponse";
ResolveFloatResponse.fields = proto3.util.newFieldList(() => [
    { no: 1, name: "value", kind: "scalar", T: 1 /* ScalarType.DOUBLE */ },
    { no: 2, name: "reason", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 3, name: "variant", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 4, name: "metadata", kind: "message", T: Struct },
]);
/**
 * Request body for int flag evaluation, used by the ResolveInt rpc.
 *
 * @generated from message schema.v1.ResolveIntRequest
 */
class ResolveIntRequest extends Message {
    constructor(data) {
        super();
        /**
         * Flag key of the requested flag.
         *
         * @generated from field: string flag_key = 1;
         */
        this.flagKey = "";
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new ResolveIntRequest().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new ResolveIntRequest().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new ResolveIntRequest().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(ResolveIntRequest, a, b);
    }
}
ResolveIntRequest.runtime = proto3;
ResolveIntRequest.typeName = "schema.v1.ResolveIntRequest";
ResolveIntRequest.fields = proto3.util.newFieldList(() => [
    { no: 1, name: "flag_key", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 2, name: "context", kind: "message", T: Struct },
]);
/**
 * Response body for int flag evaluation. used by the ResolveInt rpc.
 *
 * @generated from message schema.v1.ResolveIntResponse
 */
class ResolveIntResponse extends Message {
    constructor(data) {
        super();
        /**
         * The response value of the int flag evaluation, will be unset in the case of error.
         *
         * @generated from field: int64 value = 1;
         */
        this.value = protoInt64.zero;
        /**
         * The reason for the given return value, see https://openfeature.dev/docs/specification/types#resolution-details
         *
         * @generated from field: string reason = 2;
         */
        this.reason = "";
        /**
         * The variant name of the returned flag value.
         *
         * @generated from field: string variant = 3;
         */
        this.variant = "";
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new ResolveIntResponse().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new ResolveIntResponse().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new ResolveIntResponse().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(ResolveIntResponse, a, b);
    }
}
ResolveIntResponse.runtime = proto3;
ResolveIntResponse.typeName = "schema.v1.ResolveIntResponse";
ResolveIntResponse.fields = proto3.util.newFieldList(() => [
    { no: 1, name: "value", kind: "scalar", T: 3 /* ScalarType.INT64 */ },
    { no: 2, name: "reason", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 3, name: "variant", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 4, name: "metadata", kind: "message", T: Struct },
]);
/**
 * Request body for object flag evaluation, used by the ResolveObject rpc.
 *
 * @generated from message schema.v1.ResolveObjectRequest
 */
class ResolveObjectRequest extends Message {
    constructor(data) {
        super();
        /**
         * Flag key of the requested flag.
         *
         * @generated from field: string flag_key = 1;
         */
        this.flagKey = "";
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new ResolveObjectRequest().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new ResolveObjectRequest().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new ResolveObjectRequest().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(ResolveObjectRequest, a, b);
    }
}
ResolveObjectRequest.runtime = proto3;
ResolveObjectRequest.typeName = "schema.v1.ResolveObjectRequest";
ResolveObjectRequest.fields = proto3.util.newFieldList(() => [
    { no: 1, name: "flag_key", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 2, name: "context", kind: "message", T: Struct },
]);
/**
 * Response body for object flag evaluation. used by the ResolveObject rpc.
 *
 * @generated from message schema.v1.ResolveObjectResponse
 */
class ResolveObjectResponse extends Message {
    constructor(data) {
        super();
        /**
         * The reason for the given return value, see https://openfeature.dev/docs/specification/types#resolution-details
         *
         * @generated from field: string reason = 2;
         */
        this.reason = "";
        /**
         * The variant name of the returned flag value.
         *
         * @generated from field: string variant = 3;
         */
        this.variant = "";
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new ResolveObjectResponse().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new ResolveObjectResponse().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new ResolveObjectResponse().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(ResolveObjectResponse, a, b);
    }
}
ResolveObjectResponse.runtime = proto3;
ResolveObjectResponse.typeName = "schema.v1.ResolveObjectResponse";
ResolveObjectResponse.fields = proto3.util.newFieldList(() => [
    { no: 1, name: "value", kind: "message", T: Struct },
    { no: 2, name: "reason", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 3, name: "variant", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 4, name: "metadata", kind: "message", T: Struct },
]);
/**
 * Response body for the EventStream stream response
 *
 * @generated from message schema.v1.EventStreamResponse
 */
class EventStreamResponse extends Message {
    constructor(data) {
        super();
        /**
         * String key indicating the type of event that is being received, for example, provider_ready or configuration_change
         *
         * @generated from field: string type = 1;
         */
        this.type = "";
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new EventStreamResponse().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new EventStreamResponse().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new EventStreamResponse().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(EventStreamResponse, a, b);
    }
}
EventStreamResponse.runtime = proto3;
EventStreamResponse.typeName = "schema.v1.EventStreamResponse";
EventStreamResponse.fields = proto3.util.newFieldList(() => [
    { no: 1, name: "type", kind: "scalar", T: 9 /* ScalarType.STRING */ },
    { no: 2, name: "data", kind: "message", T: Struct },
]);
/**
 * Empty stream request body
 *
 * @generated from message schema.v1.EventStreamRequest
 */
class EventStreamRequest extends Message {
    constructor(data) {
        super();
        proto3.util.initPartial(data, this);
    }
    static fromBinary(bytes, options) {
        return new EventStreamRequest().fromBinary(bytes, options);
    }
    static fromJson(jsonValue, options) {
        return new EventStreamRequest().fromJson(jsonValue, options);
    }
    static fromJsonString(jsonString, options) {
        return new EventStreamRequest().fromJsonString(jsonString, options);
    }
    static equals(a, b) {
        return proto3.util.equals(EventStreamRequest, a, b);
    }
}
EventStreamRequest.runtime = proto3;
EventStreamRequest.typeName = "schema.v1.EventStreamRequest";
EventStreamRequest.fields = proto3.util.newFieldList(() => []);

//*
/**
 * Service defines the exposed rpcs of flagd
 *
 * @generated from service schema.v1.Service
 */
const Service = {
    typeName: "schema.v1.Service",
    methods: {
        /**
         * @generated from rpc schema.v1.Service.ResolveAll
         */
        resolveAll: {
            name: "ResolveAll",
            I: ResolveAllRequest,
            O: ResolveAllResponse,
            kind: MethodKind.Unary,
        },
        /**
         * @generated from rpc schema.v1.Service.ResolveBoolean
         */
        resolveBoolean: {
            name: "ResolveBoolean",
            I: ResolveBooleanRequest,
            O: ResolveBooleanResponse,
            kind: MethodKind.Unary,
        },
        /**
         * @generated from rpc schema.v1.Service.ResolveString
         */
        resolveString: {
            name: "ResolveString",
            I: ResolveStringRequest,
            O: ResolveStringResponse,
            kind: MethodKind.Unary,
        },
        /**
         * @generated from rpc schema.v1.Service.ResolveFloat
         */
        resolveFloat: {
            name: "ResolveFloat",
            I: ResolveFloatRequest,
            O: ResolveFloatResponse,
            kind: MethodKind.Unary,
        },
        /**
         * @generated from rpc schema.v1.Service.ResolveInt
         */
        resolveInt: {
            name: "ResolveInt",
            I: ResolveIntRequest,
            O: ResolveIntResponse,
            kind: MethodKind.Unary,
        },
        /**
         * @generated from rpc schema.v1.Service.ResolveObject
         */
        resolveObject: {
            name: "ResolveObject",
            I: ResolveObjectRequest,
            O: ResolveObjectResponse,
            kind: MethodKind.Unary,
        },
        /**
         * @generated from rpc schema.v1.Service.EventStream
         */
        eventStream: {
            name: "EventStream",
            I: EventStreamRequest,
            O: EventStreamResponse,
            kind: MethodKind.ServerStreaming,
        },
    }
};

const DEFAULT_MAX_DELAY = 60000;
function getOptions(options) {
    return Object.assign({
        port: 443,
        tls: true,
        maxRetries: 0,
        maxDelay: DEFAULT_MAX_DELAY,
        pathPrefix: '',
    }, options);
}

const ERROR_DISABLED = 'DISABLED';
const EVENT_CONFIGURATION_CHANGE = 'configuration_change';
const EVENT_PROVIDER_READY = 'provider_ready';
const BACK_OFF_MULTIPLIER = 2;
const INITIAL_DELAY_MS = 100;
class FlagdWebProvider {
    constructor(options, logger, promiseClient, callbackClient) {
        this.metadata = {
            name: 'flagd-web',
        };
        this._status = ProviderStatus.NOT_READY;
        this._connected = false;
        this._retry = 0;
        this._delayMs = INITIAL_DELAY_MS;
        this._flags = {};
        this.events = new OpenFeatureEventEmitter();
        const { host, port, tls, maxRetries, maxDelay, pathPrefix } = getOptions(options);
        const transport = createConnectTransport({
            baseUrl: `${tls ? 'https' : 'http'}://${host}:${port}/${pathPrefix}`,
        });
        this._promiseClient = promiseClient ? promiseClient : createPromiseClient(Service, transport);
        this._callbackClient = callbackClient ? callbackClient : createCallbackClient(Service, transport);
        this._maxRetries = maxRetries === 0 ? Infinity : maxRetries;
        this._maxDelay = maxDelay;
        this._logger = logger;
    }
    get status() {
        return this._status;
    }
    onContextChange(oldContext, newContext) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.fetchAll(newContext);
        });
    }
    initialize() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.retryConnect();
        });
    }
    resolveBooleanEvaluation(flagKey) {
        return this.evaluate(flagKey, 'boolValue');
    }
    resolveStringEvaluation(flagKey) {
        return this.evaluate(flagKey, 'stringValue');
    }
    resolveNumberEvaluation(flagKey) {
        return this.evaluate(flagKey, 'doubleValue');
    }
    resolveObjectEvaluation(flagKey) {
        return this.evaluate(flagKey, 'objectValue');
    }
    onClose() {
        var _a;
        // close the stream using the saved cancel function
        return Promise.resolve((_a = this._cancelFunction) === null || _a === void 0 ? void 0 : _a.call(this));
    }
    evaluate(flagKey, type) {
        const resolved = this._flags[flagKey];
        if (!resolved) {
            throw new FlagNotFoundError(`flag key ${flagKey} not found in cache`);
        }
        if (resolved.type !== type) {
            throw new TypeMismatchError(`flag key ${flagKey} is not of type ${type}`);
        }
        return {
            // return reason=CACHED if we're disconnected since we can't guarantee things are up to date
            reason: this._connected ? resolved.reason : StandardResolutionReasons.CACHED,
            variant: resolved.variant,
            value: resolved.value,
        };
    }
    retryConnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this._delayMs = Math.min(this._delayMs * BACK_OFF_MULTIPLIER, this._maxDelay);
            return new Promise((resolve) => {
                this._cancelFunction = this._callbackClient.eventStream({}, (message) => {
                    var _a;
                    // get the context at the time of the message
                    const currentContext = OpenFeature.getContext();
                    (_a = this._logger) === null || _a === void 0 ? void 0 : _a.debug(`${FlagdWebProvider.name}: event received: ${message.type}`);
                    switch (message.type) {
                        case EVENT_PROVIDER_READY:
                            this.fetchAll(currentContext).then(() => {
                                this.resetConnectionState();
                                this._status = ProviderStatus.READY;
                                resolve();
                            });
                            return;
                        case EVENT_CONFIGURATION_CHANGE: {
                            this.fetchAll(currentContext).then(() => {
                                this.events.emit(ProviderEvents.ConfigurationChanged);
                            });
                            return;
                        }
                    }
                }, (err) => {
                    var _a, _b, _c;
                    this._status = ProviderStatus.ERROR;
                    (_a = this._logger) === null || _a === void 0 ? void 0 : _a.error(`${FlagdWebProvider.name}: could not establish connection to flagd, ${err === null || err === void 0 ? void 0 : err.message}`);
                    (_b = this._logger) === null || _b === void 0 ? void 0 : _b.debug(err === null || err === void 0 ? void 0 : err.stack);
                    if (this._retry < this._maxRetries) {
                        this._retry++;
                        setTimeout(() => this.retryConnect(), this._delayMs);
                    }
                    else {
                        (_c = this._logger) === null || _c === void 0 ? void 0 : _c.warn(`${FlagdWebProvider.name}: max retries reached`);
                        this.events.emit(ProviderEvents.Error);
                    }
                });
            });
        });
    }
    fetchAll(context) {
        return __awaiter(this, void 0, void 0, function* () {
            const transformedContext = this.transformContext(context);
            const allResolved = yield this._promiseClient.resolveAll({ context: transformedContext });
            this._flags = Object.keys(allResolved.flags).reduce((accumuated, currentKey) => {
                const resolved = allResolved.flags[currentKey];
                // reducer to store the resolved bulk response in a map of ResolutionDetails,
                // with an addition annotation for the type (typeof AnyFlag.prototype.value.case)
                return Object.assign(Object.assign({}, accumuated), { [currentKey]: {
                        type: resolved.value.case,
                        reason: resolved.reason,
                        variant: resolved.variant,
                        // if it's an object, we have to parse it.
                        value: resolved.value.case === 'objectValue' ? resolved.value.value.toJson() : resolved.value.value,
                    } });
            }, {});
        });
    }
    resetConnectionState() {
        this._retry = 0;
        this._delayMs = INITIAL_DELAY_MS;
        this._connected = true;
    }
    transformContext(context) {
        return Struct.fromJson(context);
    }
}

export { ERROR_DISABLED, FlagdWebProvider };
